import type { ProjectRole, Role } from '../api/types'

/** A person's project roles in the user list (Tamar, PM-33): one choice per
 *  project. The choices are `projects.check_accounts`' rules as the person
 *  sees them, and the server applies them again: an active account (viewer or
 *  admin) may be given any role; **one that has gone pending or disabled since
 *  joining may be lowered or taken out, never raised**, and cannot be added. */

const RANK: Record<ProjectRole, number> = { viewer: 1, editor: 2, admin: 3 }
const LABEL: Record<ProjectRole, string> = { viewer: 'Viewer', editor: 'Editor', admin: 'Admin' }

export interface RoleChoice { value: '' | ProjectRole; label: string }

export const isActiveAccount = (account: Role): boolean => account === 'viewer' || account === 'admin'

export function projectRoleChoices(account: Role, held: ProjectRole | null): RoleChoice[] {
  const roles = (Object.keys(RANK) as ProjectRole[])
    .filter((role) => isActiveAccount(account) || (held !== null && RANK[role] <= RANK[held]))
  return [{ value: '', label: 'No membership' }, ...roles.map((role) => ({ value: role, label: LABEL[role] }))]
}

/** Whether there is anything to choose: an account that is not active and
 *  holds no role in the project has only "No membership". */
export const canChangeRole = (account: Role, held: ProjectRole | null): boolean =>
  projectRoleChoices(account, held).length > 1 || held !== null

/** The role a person holds in a project's member list, or null. */
export function heldRole(members: readonly { user_id: number; role: ProjectRole }[], userId: number): ProjectRole | null {
  return members.find((m) => m.user_id === userId)?.role ?? null
}
